import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountSidebar from '../components/account/AccountSidebar';
import AccountInfo from '../components/account/AccountInfo';
import OrderHistory from '../components/account/OrderHistory';
import Returns from '../components/account/Returns';
import Communications from '../components/account/Communications';
import LoginModal from '../components/account/LoginModal';
import { useCustomer } from 'context/CustomerContext';
import { getMyDistributorStatus } from 'services/medusa/distributor';
import SEO from '../components/SEO';
import './Account.css';

const AccountDashboard = () => {
  const { customer, isAuthenticated, loading, logout, updateCustomer, refreshCustomer, token } = useCustomer();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('account-info');
  const [showLogin, setShowLogin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [distStatus, setDistStatus] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getMyDistributorStatus(token)
      .then((s) => { if (!cancelled) setDistStatus(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const isDistributor = Boolean(distStatus?.is_distributor);

  // Si es distribuidora aprobada, el panel exclusivo vive en /socio.
  useEffect(() => {
    if (!loading && isAuthenticated && isDistributor) {
      navigate('/socio', { replace: true });
    }
  }, [loading, isAuthenticated, isDistributor, navigate]);

  const menuOptions = [
    { id: 'account-info', label: 'Escritorio' },
    { id: 'order-history', label: 'Historial de pedidos' },
    { id: 'returns', label: 'Devoluciones' },
    { id: 'communications', label: 'Comunicaciones' },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
  };

  const handleMobileMenuSelect = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  // Obtener nombre del cliente
  const getCustomerName = () => {
    if (!customer) return '';
    const firstName = customer.first_name || '';
    const lastName = customer.last_name || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return customer.email || '';
  };

  // Obtener label actual del menú
  const getCurrentMenuLabel = () => {
    const current = menuOptions.find(opt => opt.id === activeSection);
    return current?.label || 'Escritorio';
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'account-info':
        return (
          <AccountInfo
            customer={customer}
            updateCustomer={updateCustomer}
            refreshCustomer={refreshCustomer}
          />
        );
      case 'order-history':
        return <OrderHistory />;
      case 'returns':
        return <Returns />;
      case 'communications':
        return (
          <Communications
            customer={customer}
            updateCustomer={updateCustomer}
          />
        );
      default:
        return (
          <AccountInfo
            customer={customer}
            updateCustomer={updateCustomer}
            refreshCustomer={refreshCustomer}
          />
        );
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="account-dashboard">
        <header className="account-dashboard__header">
          <h1 className="account-dashboard__welcome">Cargando...</h1>
        </header>
        <div className="account-dashboard__container">
          <div className="account-dashboard__loading">
            <p>Obteniendo información de tu cuenta...</p>
          </div>
        </div>
      </div>
    );
  }

  // No hay customer - mostrar login
  if (!isAuthenticated) {
    return (
      <>
        <div className="account-dashboard">
          <header className="account-dashboard__header">
            <h1 className="account-dashboard__welcome">Mi Cuenta</h1>
          </header>
          <div className="account-dashboard__container">
            <div className="account-dashboard__login-prompt">
              <p>Inicia sesión para acceder a tu cuenta</p>
              <button
                className="account-dashboard__login-btn"
                onClick={() => setShowLogin(true)}
              >
                Iniciar sesión
              </button>
            </div>
          </div>
        </div>
        <LoginModal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </>
    );
  }

  const customerName = getCustomerName();

  return (
    <div className="account-dashboard">
      <SEO title="Mi cuenta" description="Panel personal de cuenta Physiology Skincare Co." noindex />
      <header className="account-dashboard__header">
        <h1 className="account-dashboard__welcome">
          Hola, {customerName}
        </h1>
      </header>

      <div className="account-dashboard__container">
        {/* Mobile Dropdown Menu */}
        <div className="account-dashboard__mobile-menu">
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span>{getCurrentMenuLabel()}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              className={mobileMenuOpen ? 'rotated' : ''}
            >
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          </button>

          {mobileMenuOpen && (
            <div className="mobile-menu-dropdown">
              {menuOptions.map((option) => (
                <button
                  key={option.id}
                  className={`mobile-menu-item ${activeSection === option.id ? 'active' : ''}`}
                  onClick={() => handleMobileMenuSelect(option.id)}
                >
                  {option.label}
                </button>
              ))}
              <button
                className="mobile-menu-item logout"
                onClick={handleLogout}
              >
                Cerrar sesión
              </button>
            </div>
          )}
        </div>

        <div className="account-dashboard__layout">
          {/* Desktop Sidebar */}
          <div className="account-dashboard__sidebar-desktop">
            <AccountSidebar
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              onLogout={handleLogout}
              menuItems={menuOptions}
            />
          </div>

          <main className="account-dashboard__content">
            {renderSection()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;
